"use client";

import { useEffect, useRef, useState } from "react";
import { MapPin, Layers, Shirt, ShieldCheck } from "lucide-react";
import { Pentagon3D } from "./Pentagon3D";

const reasons = [
  {
    icon: MapPin,
    heading: "UK Based Team",
    body: "Based in the UK, built for UK clothing founders",
    stat: "Birmingham HQ",
  },
  {
    icon: Layers,
    heading: "End-to-End Service",
    body: "Branding, Shopify, SEO and copy — one team, one timeline",
    stat: "4 services, 1 team",
  },
  {
    icon: Shirt,
    heading: "Clothing Niche Only",
    body: "We don't work with everyone — just clothing brand founders",
    stat: "100% fashion focus",
  },
  {
    icon: ShieldCheck,
    heading: "No Long Contracts",
    body: "Month to month. Stay because it's working, not because you're locked in",
    stat: "Month to month",
  },
];

export function WhyOkPromo() {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative border-b border-border overflow-hidden"
      style={{ backgroundColor: "#000000" }}
    >
      {/* Floating 3D orb — left side, biased toward the top of the section */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-2 lg:left-6 top-[28%] -translate-y-1/2 opacity-90 mix-blend-screen z-0 hidden md:block"
        style={{ filter: "drop-shadow(0 0 60px rgba(191,247,69,0.45))" }}
      >
        <Pentagon3D
          size={300}
          speedX={0.04}
          speedY={0.10}
          floatAmp={0.5}
          floatSpeed={0.18}
        />
      </div>

      {/* Subtle grid/dot pattern */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(rgba(191,247,69,0.6) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          opacity: 0.03,
        }}
      />

      <div className="relative site-container py-24 lg:py-32">
        <div className="mb-14 lg:mb-20">
          <p
            className="text-small font-semibold uppercase tracking-[0.2em]"
            style={{ color: "#bff745" }}
          >
            <span aria-hidden="true" className="mr-2">*</span>Why OK Promo
          </p>
          <h2 className="text-h2 mt-4 max-w-3xl">
            Built different.{" "}
            <span style={{ color: "#bff745" }}>For clothing brands only.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {reasons.map(({ icon: Icon, heading, body, stat }, i) => (
            <article
              key={heading}
              className="group/card relative isolate rounded-lg overflow-hidden p-7 lg:p-8 transition-all duration-300 ease-out hover:-translate-y-1"
              style={{
                backgroundColor: "#111111",
                border: "1px solid #1E1E1E",
                opacity: inView ? 1 : 0,
                transform: inView
                  ? "translateY(0)"
                  : "translateY(16px)",
                transition:
                  "opacity 500ms ease-out, transform 500ms ease-out, border-color 300ms ease",
                transitionDelay: `${i * 150}ms`,
              }}
            >
              {/* Sliding bottom border on hover */}
              <span
                aria-hidden="true"
                className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 transition-transform duration-300 ease-out group-hover/card:scale-x-100"
                style={{ backgroundColor: "#bff745" }}
              />

              {/* Icon block */}
              <div className="relative inline-flex items-center justify-center">
                <span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-md opacity-0 transition-opacity duration-300 group-hover/card:opacity-100"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(191,247,69,0.45) 0%, rgba(191,247,69,0) 70%)",
                    transform: "scale(2.2)",
                    filter: "blur(8px)",
                  }}
                />
                <span
                  className="relative inline-flex size-12 items-center justify-center rounded-md transition-transform duration-300 group-hover/card:scale-[1.12]"
                  style={{
                    backgroundColor: "#0a0a0a",
                    border: "1px solid #1E1E1E",
                    animation: `whyokpromo-float 4s ease-in-out ${i * 0.2}s infinite`,
                  }}
                >
                  <Icon
                    aria-hidden="true"
                    style={{ width: 24, height: 24, color: "#bff745" }}
                    strokeWidth={1.75}
                  />
                </span>
              </div>

              <h3 className="mt-6 text-h3 font-bold text-text transition-colors duration-300 group-hover/card:text-white">
                {heading}
              </h3>
              <p className="mt-3 text-small" style={{ color: "#888888" }}>
                {body}
              </p>

              {/* Stat pill */}
              <div className="mt-6">
                <span
                  className="inline-flex items-center rounded-pill px-3 py-1 text-[0.6875rem] font-bold uppercase tracking-wider"
                  style={{
                    color: "#bff745",
                    backgroundColor: "rgba(191,247,69,0.10)",
                    border: "1px solid rgba(191,247,69,0.25)",
                  }}
                >
                  {stat}
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes whyokpromo-float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-4px) rotate(2deg);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          :global(.group\\/card span[style*="whyokpromo-float"]) {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
}
